import React, { forwardRef, useState } from "react";
import { Tab } from "@/types";
import { cn } from "@/lib/utils";
import { Pencil, Trash2 } from "lucide-react";
import EditTabModal from "./EditTabModal";
import ConfirmDeleteDialog from "../common/ConfirmDeleteDialog";

interface TabCardProps {
  tab: Tab;
  onClick?: () => void;
  className?: string;
  collectionId?: string;
  isOpenTab?: boolean;
  isDragging?: boolean;
  isDragHandle?: boolean;
  onTabEdit?: (tabId: string, updated: Partial<Tab> & { favicon_url?: string }) => void;
  onTabDelete?: (tab: Tab) => void;
  // react-beautiful-dnd props
  [key: string]: any;
}

const TabCard = forwardRef<HTMLDivElement, TabCardProps>(
  (
    {
      tab,
      onClick,
      className,
      collectionId,
      isOpenTab = false,
      isDragging = false,
      isDragHandle = false,
      onTabEdit,
      onTabDelete,
      ...props
    },
    ref
  ) => {
    const [editOpen, setEditOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);

    const openTabInNewWindow = (e: React.MouseEvent) => {
      // Only open if not clicking the edit or delete icon
      if ((e.target as HTMLElement).closest('.tab-edit-icon, .tab-delete-icon')) return;
      e.preventDefault();
      e.stopPropagation();
      if (import.meta.env.MODE === "development") {
        window.open(tab.url, '_blank');
      } else if (typeof chrome !== "undefined" && chrome.tabs && typeof chrome.tabs.create === "function") {
        chrome.tabs.create({ url: tab.url });
      } else {
        window.open(tab.url, '_blank');
      }
    };

    const handleEditClick = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setEditOpen(true);
    };

    const handleEditSave = (updated: Partial<Tab> & { favicon_url?: string }) => {
      if (onTabEdit) {
        onTabEdit(tab.tab_id, updated);
      }
    };

    const handleDeleteClick = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDeleteOpen(true);
    };

    const handleDeleteConfirm = () => {
      if (onTabDelete) {
        onTabDelete(tab);
      }
      setDeleteOpen(false);
    };

    return (
      <>
        <div
          ref={ref}
          {...props}
          data-dragging={isDragging}
          title="Click to open URL. Drag card to move."
          className={cn(
            "group relative select-none flex flex-col gap-3 p-4 rounded-lg bg-muted/50 hover:bg-accent/40 border border-border/10 transition-colors duration-200 hover:shadow-lg hover:shadow-accent/5 active:shadow-md h-full cursor-pointer",
            isDragging && "ring-2 ring-hintword-orange/30 shadow-xl scale-[1.02] bg-accent/40",
            className
          )}
          onClick={openTabInNewWindow}
        >
          <div className="flex items-center gap-3">
            {/* Vertically aligned edit and delete icons on the left, only on hover */}
            {(onTabEdit || onTabDelete) && (
              <div className="hidden group-hover:flex flex-col items-center justify-center gap-2 mr-2">
                {onTabEdit && (
                  <button
                    className="tab-edit-icon inline-flex items-center p-1 rounded hover:bg-accent/30"
                    title="Edit tab"
                    onClick={handleEditClick}
                    tabIndex={-1}
                    type="button"
                  >
                    <Pencil size={16} />
                  </button>
                )}
                {onTabDelete && (
                  <button
                    className="tab-delete-icon inline-flex items-center p-1 rounded hover:bg-destructive/10"
                    title="Delete tab"
                    onClick={handleDeleteClick}
                    tabIndex={-1}
                    type="button"
                  >
                    <Trash2 size={16} className="text-destructive" />
                  </button>
                )}
              </div>
            )}
            {tab.favicon_url ? (
              <img
                src={tab.favicon_url}
                alt=""
                className="w-8 h-8 rounded-lg object-contain bg-accent p-1.5"
              />
            ) : (
              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-accent text-foreground font-medium text-sm shadow-sm">
                {tab.title.charAt(0).toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-medium text-foreground truncate">{tab.title}</h3>
              </div>
              <div className="flex items-center gap-2">
                <p className="text-xs text-muted-foreground truncate">{tab.url}</p>
              </div>
            </div>
          </div>
        </div>
        {onTabEdit && (
          <EditTabModal
            open={editOpen}
            onOpenChange={setEditOpen}
            tab={tab}
            onSave={handleEditSave}
          />
        )}
        {onTabDelete && (
          <ConfirmDeleteDialog
            open={deleteOpen}
            title="Delete Tab"
            description={`Are you sure you want to delete "${tab.title}"? This will set the tab as inactive.`}
            onCancel={() => setDeleteOpen(false)}
            onConfirm={handleDeleteConfirm}
            confirmLabel="Delete"
            cancelLabel="Cancel"
          />
        )}
      </>
    );
  }
);

export default TabCard;
